import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IMatter } from '../interface/matter';
import {
  IShowDialogProps,
  ITransactionRestrictions,
  TransactionAlertSource,
} from '../interface/transactionRestriction';
import { InvoiceableType } from '../constants/transaction';
import { AlertType } from '../interface/parameters';
import {
  getCustomerIdentificationRestrictionMessage,
  getOverDueInvoicesMessage,
  getServiceRestrictionMessage,
} from '../helpers/transactionRestrictionMessageHelper';
interface IUseGetNewTransactionRestrictions {
  selectedMatter: IMatter | undefined;
  onSelect: (matter: IMatter, alertType?: AlertType) => void;
  matterTransactionRestrictions?: ITransactionRestrictions;
}

export function useHandleTransactionRestrictions({
  selectedMatter,
  matterTransactionRestrictions,
  onSelect,
}: IUseGetNewTransactionRestrictions) {
  const { t } = useTranslation();
  const [serviceRestrictionErrorDialog, setServiceRestrictionErrorDialog] =
    React.useState<IShowDialogProps>({
      visible: false,
      message: '',
    });

  const [customerIdentificationErrorDialog, setCustomerIdentificationErrorDialog] =
    React.useState<IShowDialogProps>({
      visible: false,
      message: '',
    });

  useEffect(() => {
    if (!matterTransactionRestrictions) {
      return;
    }

    if (matterTransactionRestrictions?.matterId !== selectedMatter?.id) {
      return;
    }
    if (
      matterTransactionRestrictions.customerIdentification.hasIssue &&
      matterTransactionRestrictions.customerIdentification.alertType === AlertType.ERROR
    ) {
      setCustomerIdentificationErrorDialog({
        visible: true,
        message: getCustomerIdentificationRestrictionMessage(t),
      });
    } else {
      handleServiceRestrictionChecks(matterTransactionRestrictions, false, selectedMatter);
    }
  }, [matterTransactionRestrictions]);

  const handleServiceRestrictionHasIssue = (
    alertType: AlertType,
    scope: TransactionAlertSource,
    identificationDialogWasShownBefore: boolean,
    customerIdentificationAlertType: AlertType | undefined,
    matter: IMatter,
  ) => {
    if (alertType === AlertType.ERROR) {
      setTimeout(
        () =>
          setServiceRestrictionErrorDialog({
            visible: true,
            message: getServiceRestrictionMessage(t, scope),
            previousRestrictionAlertType: customerIdentificationAlertType,
          }),
        identificationDialogWasShownBefore ? 500 : 0,
      );
    } else {
      onSelect(matter, customerIdentificationAlertType);
    }
  };
  const handleTransactionOverDueInvoicesChecks = (
    alertType: AlertType,
    scope: TransactionAlertSource,
    minimumAllowedOverDueDaysBeforeAlert: number,
    identificationDialogWasShownBefore: boolean,
    matter: IMatter,
    customerIdentificationAlertType?: AlertType,
  ) => {
    if (alertType === AlertType.ERROR) {
      setTimeout(
        () =>
          setServiceRestrictionErrorDialog({
            visible: true,
            message: getOverDueInvoicesMessage(t, scope, minimumAllowedOverDueDaysBeforeAlert),
          }),
        identificationDialogWasShownBefore ? 500 : 0,
      );
    } else {
      onSelect(matter, customerIdentificationAlertType);
    }
  };
  const handleServiceRestrictionChecks = (
    transactionRestrictions: ITransactionRestrictions,
    identificationDialogWasShownBefore: boolean,
    matter: IMatter,
    customerIdentificationAlertType?: AlertType,
  ) => {
    if (matter.invoiceableType === InvoiceableType.INTERNAL) {
      onSelect(matter, customerIdentificationAlertType);
      return;
    }
    if (transactionRestrictions.serviceRestriction.hasIssue) {
      const { alertType, scope } = transactionRestrictions.serviceRestriction;
      handleServiceRestrictionHasIssue(
        alertType,
        scope,
        identificationDialogWasShownBefore,
        customerIdentificationAlertType,
        matter,
      );
    } else if (transactionRestrictions.overDueInvoices.hasIssue) {
      const { alertType, scope, minimumAllowedOverDueDaysBeforeAlert } =
        transactionRestrictions.overDueInvoices;
      handleTransactionOverDueInvoicesChecks(
        alertType,
        scope,
        minimumAllowedOverDueDaysBeforeAlert,
        identificationDialogWasShownBefore,
        matter,
        customerIdentificationAlertType,
      );
    } else {
      onSelect(matter, customerIdentificationAlertType);
    }
  };

  return {
    matterTransactionRestrictions,
    handleServiceRestrictionChecks,
    serviceRestrictionErrorDialog,
    setServiceRestrictionErrorDialog,
    customerIdentificationErrorDialog,
    setCustomerIdentificationErrorDialog,
  };
}
